import React, { useState, useEffect, useCallback } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
  PreviewCard,
} from "../../../components/Component";
import { useNavigate } from "react-router-dom";
import FormInput from "../../form-components/InputComponent";
import { useForm, FormProvider } from "react-hook-form";
import { Col, Row, Alert } from "reactstrap";
import { Checkbox, Collapse, Tag, Empty } from "antd";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";
import type {
  ModuleTreeGroup,
} from "../../../types/super-admin";

const AppCreate: React.FC = () => {
  const navigate = useNavigate();
  const formMethods = useForm({ mode: "onChange" });
  const { handleSubmit } = formMethods;

  const [loading, setLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [] as string[],
  });

  // Module selection state
  const [moduleTree, setModuleTree] = useState<ModuleTreeGroup[]>([]);
  const [selectedModuleIds, setSelectedModuleIds] = useState<number[]>([]);

  const fetchModuleOptions = useCallback(async () => {
    try {
      const res = await superAdminService.getModuleDropdown();
      if (res.status && res.data) {
        setModuleTree(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error: any) {
      console.error("Failed to fetch modules:", error.message);
    }
  }, []);

  useEffect(() => {
    fetchModuleOptions();
  }, [fetchModuleOptions]);

  const submitForm = async (data: any) => {
    setLoading(true);
    setAlertInfo({ type: "", message: "", errors: [] });

    try {
      const payload: any = {
        appName: data.appName?.trim(),
      };

      if (data.appCode) payload.appCode = data.appCode.trim();
      if (data.description) payload.description = data.description.trim();

      const res = await superAdminService.createApplication(payload);
      if (res.status) {
        const appId = res.data?.appId;

        // Map modules if any selected
        if (appId && selectedModuleIds.length > 0) {
          try {
            await superAdminService.mapModulesToApp({
              appId,
              moduleIds: selectedModuleIds,
            });
          } catch (mapError: any) {
            console.error("Module mapping failed:", mapError.message);
          }
        }

        Swal.fire(
          "Created!",
          "Application has been created successfully.",
          "success"
        );
        navigate("/sa-applications");
      } else {
        const errorMsg = Array.isArray(res.message)
          ? res.message.join(", ")
          : res.message || "An error occurred";
        setAlertInfo({ type: "error", message: errorMsg, errors: [] });
      }
    } catch (error: any) {
      setAlertInfo({
        type: "error",
        message: error.message || "An error occurred",
        errors: [],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Head title="Create Application" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Create Application
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>Add a new application</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                className="btn-dim"
                onClick={() => navigate("/sa-applications")}
              >
                <Icon name="menu-alt-r"></Icon>
                <span>Application List</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <PreviewCard className="cardBox" bodyClass="card-inner-xl">
            {alertInfo.message && (
              <div className="mb-3">
                <Alert
                  color={alertInfo.type === "error" ? "danger" : "success"}
                  className="alert-icon alert-animated"
                >
                  <strong>{alertInfo.message}</strong>
                  <Icon
                    name={
                      alertInfo.type === "error"
                        ? "alert-circle"
                        : "check-circle"
                    }
                  />
                </Alert>
              </div>
            )}
            <FormProvider {...formMethods}>
              <form
                className="content clearfix"
                onSubmit={handleSubmit(submitForm)}
              >
                <Row className="gy-4">
                  <Col lg="6">
                    <FormInput
                      name="appCode"
                      label="App Code (Optional)"
                      type="text"
                      maxLength={50}
                      placeholder="Enter unique app code (e.g. P2P, PMS)"
                    />
                  </Col>
                  <Col lg="6">
                    <FormInput
                      name="appName"
                      label="Application Name"
                      type="text"
                      required={true}
                      maxLength={200}
                      placeholder="Enter application name"
                    />
                  </Col>
                  <Col lg="12">
                    <FormInput
                      name="description"
                      label="Description (Optional)"
                      type="text"
                      maxLength={500}
                      placeholder="Enter application description"
                    />
                  </Col>
                  <Col lg="12">
                    <div className="form-group">
                      <label className="form-label">
                        Map Modules (Optional)
                      </label>
                      <div style={{ maxHeight: 400, overflowY: "auto", border: "1px solid #e5e9f2", borderRadius: 6, padding: 12 }}>
                        {moduleTree.length > 0 ? (() => {
                          const collect = (mods: any[]): number[] => {
                            const out: number[] = [];
                            mods.forEach((m: any) => {
                              if (m.isParent && m.children && m.children.length > 0) {
                                m.children.forEach((c: any) => out.push(c.moduleId));
                              } else {
                                out.push(m.moduleId);
                              }
                            });
                            return out;
                          };
                          const allSelectableIds = moduleTree.reduce(
                            (acc: number[], g) => acc.concat(collect(g.modules)),
                            [],
                          );
                          const allSelected =
                            allSelectableIds.length > 0 &&
                            allSelectableIds.every((id) => selectedModuleIds.includes(id));
                          const someSelected = selectedModuleIds.length > 0 && !allSelected;
                          return (
                          <>
                            <div
                              className="d-flex align-items-center justify-content-between mb-2 pb-2"
                              style={{ borderBottom: "1px solid #f0f0f0" }}
                            >
                              <Checkbox
                                indeterminate={someSelected}
                                checked={allSelected}
                                onChange={(e) =>
                                  setSelectedModuleIds(e.target.checked ? [...allSelectableIds] : [])
                                }
                              >
                                <strong>Select All</strong>
                              </Checkbox>
                              <span style={{ fontSize: 12, color: "#8094ae" }}>
                                {selectedModuleIds.length} / {allSelectableIds.length} selected
                              </span>
                            </div>
                            <Collapse
                              defaultActiveKey={moduleTree.map((g) => g.category)}
                              ghost
                              size="small"
                            >
                            {moduleTree.map((group) => {
                              const catIds = collect(group.modules);
                              const catAll =
                                catIds.length > 0 && catIds.every((id) => selectedModuleIds.includes(id));
                              const catSome = catIds.some((id) => selectedModuleIds.includes(id)) && !catAll;
                              return (
                              <Collapse.Panel
                                key={group.category}
                                header={
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                    <Checkbox
                                      indeterminate={catSome}
                                      checked={catAll}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) =>
                                        setSelectedModuleIds((prev) =>
                                          e.target.checked
                                            ? Array.from(new Set([...prev, ...catIds]))
                                            : prev.filter((id) => !catIds.includes(id)),
                                        )
                                      }
                                    />
                                    <span style={{ fontWeight: 600, fontSize: 14 }}>{group.category}</span>
                                  </span>
                                }
                              >
                                {group.modules.map((parent) => (
                                  <div key={parent.moduleId} style={{ marginBottom: 12 }}>
                                    {parent.isParent && parent.children.length > 0 ? (
                                      <>
                                        <div style={{ fontWeight: 500, color: "#555", marginBottom: 6, fontSize: 13 }}>
                                          {parent.moduleName}
                                        </div>
                                        <div style={{ paddingLeft: 20, display: "flex", flexWrap: "wrap", gap: "6px 18px" }}>
                                          {parent.children.map((child) => (
                                            <Checkbox
                                              key={child.moduleId}
                                              checked={selectedModuleIds.includes(child.moduleId)}
                                              onChange={(e) => {
                                                if (e.target.checked) {
                                                  setSelectedModuleIds((prev) => [...prev, child.moduleId]);
                                                } else {
                                                  setSelectedModuleIds((prev) => prev.filter((id) => id !== child.moduleId));
                                                }
                                              }}
                                            >
                                              {child.moduleName}
                                            </Checkbox>
                                          ))}
                                        </div>
                                      </>
                                    ) : (
                                      <Checkbox
                                        checked={selectedModuleIds.includes(parent.moduleId)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedModuleIds((prev) => [...prev, parent.moduleId]);
                                          } else {
                                            setSelectedModuleIds((prev) => prev.filter((id) => id !== parent.moduleId));
                                          }
                                        }}
                                      >
                                        {parent.moduleName}
                                      </Checkbox>
                                    )}
                                  </div>
                                ))}
                              </Collapse.Panel>
                              );
                            })}
                            </Collapse>
                          </>
                          );
                        })() : (
                          <Empty description="No modules available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                      </div>
                      {selectedModuleIds.length > 0 && (
                        <div className="mt-2">
                          <span style={{ fontSize: 12, color: "#8094ae" }}>
                            {selectedModuleIds.length} module{selectedModuleIds.length !== 1 ? "s" : ""} selected
                          </span>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col lg="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        <Button color="primary" size="md" type="submit">
                          {loading ? (
                            <LoadingSpinner size="sm" inline />
                          ) : (
                            "Create Application"
                          )}
                        </Button>
                      </li>
                      <li>
                        <a
                          href="#cancel"
                          onClick={(e) => {
                            e.preventDefault();
                            if (!loading) navigate("/sa-applications");
                          }}
                          className={`link link-light${loading ? " disabled text-muted" : ""}`}
                          style={loading ? { pointerEvents: "none" } : {}}
                        >
                          Cancel
                        </a>
                      </li>
                    </ul>
                  </Col>
                </Row>
              </form>
            </FormProvider>
          </PreviewCard>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default AppCreate;
